"use client";

import { Check, Lock } from "lucide-react";
import type { ReactNode } from "react";
import { formatTtdShort as formatLedgerShort } from "@/studio/lib/money";

/** Plan prices are TTD. Short ledger format drops the code for TTD, so add TT$. */
function formatTtdShort(cents: number | null | undefined): string {
  const s = formatLedgerShort(cents);
  return s.startsWith("$") && !/\b(USD|TTD)$/.test(s) ? `TT${s}` : s;
}
import "../studio-billing.css";
import { CurrencyToggle } from "@/components/checkout/CurrencyToggle";
import type { BillingCurrency } from "../../../../convex/lib/billingRail";
import { usdWholeDollarCents } from "../../../../convex/lib/billingRail";
import { FIXED_USD_TO_TTD } from "../../../../convex/lib/fxUsdTtd";

function formatUsdWhole(ttdCents: number): string {
  return `US$${Math.round(usdWholeDollarCents(ttdCents, FIXED_USD_TO_TTD) / 100).toLocaleString("en-US")}`;
}

/** "Choose a plan" heading + period switch. Shared so Billing and sign-up step 4 match exactly. */
export function StudioChoosePlanHead({
  currency,
  onCurrencyChange,
  hideSwitches = false,
  ...props
}: {
  /** Billing renders the switches in its floating header instead. */
  hideSwitches?: boolean;
  interval: BillingPlanInterval;
  onChange: (next: BillingPlanInterval) => void;
  maxSave: number;
  currency?: BillingCurrency;
  onCurrencyChange?: (next: BillingCurrency) => void;
}) {
  return (
    <div className="studio-billing-choose">
      <h2 className="studio-billing-choose-title">Choose a plan</h2>
      {hideSwitches ? null : <div className="studio-billing-switches">
        <StudioBillingPeriodToggle {...props} />
        {currency && onCurrencyChange ? (
          <CurrencyToggle currency={currency} onChange={onCurrencyChange} />
        ) : null}
      </div>}
    </div>
  );
}

/** Monthly / Annual switch shared by Billing and sign-up step 4. */
export function StudioBillingPeriodToggle({
  interval,
  onChange,
  maxSave,
}: {
  interval: BillingPlanInterval;
  onChange: (next: BillingPlanInterval) => void;
  maxSave: number;
}) {
  return (
    <div className="uc2-currency uc2-currency-toggle studio-billing-period-seg" role="radiogroup" aria-label="Billing period">
      <button type="button" role="radio" aria-checked={interval === "month"} data-no-press="" className={interval === "month" ? "is-selected" : ""} onClick={() => onChange("month")}>
        Monthly
      </button>
      <button type="button" role="radio" aria-checked={interval === "year"} data-no-press="" className={interval === "year" ? "is-selected" : ""} onClick={() => onChange("year")}>
        Annual
        <span className="studio-billing-period-save">Save up to {maxSave}%</span>
      </button>
    </div>
  );
}

export type BillingPlanInterval = "month" | "year";

export type BillingPlanQuoteInput = {
  monthlyPriceCents: number;
  originalMonthlyPriceCents?: number;
  discountPercent?: number;
  annualDiscountPercent?: number;
};

export type BillingPlanQuote = {
  faceMonthlyCents: number;
  discountPercent: number;
  chargeCents: number;
};

/** Same quote the billing pane shows and charges. */
export function quoteBillingPlan(
  plan: BillingPlanQuoteInput,
  interval: BillingPlanInterval,
): BillingPlanQuote {
  const face = Number(plan.originalMonthlyPriceCents ?? plan.monthlyPriceCents ?? 0);
  const discount =
    interval === "year"
      ? Number(plan.annualDiscountPercent ?? 0)
      : Number(plan.discountPercent ?? 0);
  const faceCharge = interval === "year" ? face * 12 : face;
  return {
    faceMonthlyCents: face,
    discountPercent: discount,
    chargeCents: Math.round((faceCharge * (100 - discount)) / 100),
  };
}

export function planPitch(slug: string) {
  if (slug === "plus") return "For regular production";
  if (slug === "pro") return "For heavy Studio work";
  return "For getting started";
}

/**
 * Card checklist. Every line comes from the plan record (convex/lib/studioPlans.ts)
 * or a Billing action that exists for subscribers. No per-plan feature gates exist
 * in code today, so the cards do not claim any.
 */
function planCopy(plan: { annualDiscountPercent?: number }, grantLabel: string) {
  const annual = Number(plan.annualDiscountPercent ?? 0);
  return [
    `${grantLabel} added to your balance every month`,
    ...(annual > 0 ? [`Save ${annual}% when you pay yearly`] : []),
    "Yearly billing still adds your balance monthly",
    "Switch plans or cancel anytime",
  ];
}

export function StudioPlanCards({
  stack = false,
  children,
  beforeTrust,
}: {
  /** Rendered between the cards and the trust footer (sign-up: Pay later). */
  beforeTrust?: ReactNode;
  /** One column. Sign-up uses this so the cards fit a 360px sheet. */
  stack?: boolean;
  children: ReactNode;
}) {
  return (
    <>
      <div className={`studio-billing-plans${stack ? " is-stack" : ""}`}>{children}</div>
      {beforeTrust}
      <StudioPlanTrustFooter />
    </>
  );
}

/** Muted trust footer under the plan cards (sign-up and Billing). */
export function StudioPlanTrustFooter() {
  return (
    <div className="studio-plan-trust">
      <p className="studio-plan-trust-line">
        <Lock size={12} strokeWidth={2.25} aria-hidden="true" />
        <span>Secure payments by PayPal and Wam</span>
      </p>
      <div className="studio-plan-trust-marks" aria-label="Visa, Mastercard, American Express, PayPal, Wam">
        <img src="/branding/cards/visa.svg" alt="Visa" width={24} height={16} />
        <img src="/branding/cards/mastercard.svg" alt="Mastercard" width={24} height={16} />
        <img src="/branding/cards/amex.svg" alt="American Express" width={24} height={16} />
        <span className="studio-plan-trust-chip is-paypal"><img src="/branding/paypal.svg" alt="PayPal" height={10} /></span>
        <span className="studio-plan-trust-chip is-wam"><img src="/branding/wam.svg" alt="Wam" height={10} /></span>
      </div>
      <p className="studio-plan-trust-line">
        <span>Cancel or switch anytime</span>
        <span aria-hidden="true">|</span>
        <a href="/legal/terms/">Terms</a>
        <a href="/legal/privacy/">Privacy</a>
      </p>
    </div>
  );
}

export function StudioPlanCard({
  plan,
  interval,
  quote,
  featured = false,
  current = false,
  recommended = false,
  ending = false,
  showLocked = false,
  cancelWhen,
  selected = false,
  onSelect,
  children,
  usd = false,
}: {
  plan: { name: string; slug: string };
  interval: BillingPlanInterval;
  quote: BillingPlanQuote;
  featured?: boolean;
  current?: boolean;
  recommended?: boolean;
  ending?: boolean;
  showLocked?: boolean;
  cancelWhen?: string;
  selected?: boolean;
  /** Sign-up: the card itself selects the plan. Billing leaves this unset and passes the action as children. */
  onSelect?: () => void;
  children?: ReactNode;
  /** Outside Trinidad and Tobago: whole-dollar USD, same helper as checkout and PayPal. */
  usd?: boolean;
}) {
  const money = (cents: number) => (usd ? formatUsdWhole(cents) : formatTtdShort(cents));
  const faceYearCents = quote.faceMonthlyCents * (interval === "year" ? 12 : 1);
  const grant = money(quote.faceMonthlyCents);
  const className = `studio-billing-card${featured ? " is-featured" : ""}${current ? " is-current" : ""}${selected ? " is-selected" : ""}`;
  const body = (
    <>
      <div className="studio-billing-card-kicker">
        <div>
          <div className="studio-billing-card-title-row">
            <h3>{plan.name}</h3>
            {quote.discountPercent > 0 ? (
              <span className="studio-billing-save">Save {quote.discountPercent}%</span>
            ) : null}
          </div>
          <p className="studio-billing-pitch">{planPitch(plan.slug)}</p>
        </div>
        {current ? (
          <span className="studio-billing-badge is-current">Current</span>
        ) : recommended ? (
          <span className="studio-billing-badge">Recommended</span>
        ) : featured ? (
          <span className="studio-billing-badge">Most popular</span>
        ) : null}
      </div>
      <div className="studio-billing-price-block">
        <p className="studio-billing-price">
          {quote.discountPercent > 0 ? (
            <s className="studio-academy-card-compare">{money(faceYearCents)}</s>
          ) : null}
          <span className="studio-billing-price-now">
            {money(quote.chargeCents)}
            <span className="studio-billing-price-unit">{interval === "year" ? "/yr" : "/mo"}</span>
          </span>
        </p>
      </div>
      {showLocked ? (
        <p className="studio-billing-equiv is-locked">Your price · locked while you stay on this plan</p>
      ) : null}
      {ending ? (
        <p className="studio-billing-equiv">Cancels {cancelWhen ?? "at period end"}</p>
      ) : interval === "year" ? (
        <p className="studio-billing-equiv">
          {usd ? `US$${Math.round(usdWholeDollarCents(quote.chargeCents, FIXED_USD_TO_TTD) / 1200)}` : formatTtdShort(Math.round(quote.chargeCents / 12))}/mo billed yearly
        </p>
      ) : (
        <p className="studio-billing-equiv">{grant} credited every month</p>
      )}
      {!ending && interval === "year" ? (
        <p className="studio-billing-equiv is-sub">{grant} still lands each month</p>
      ) : null}
      <ul>
        {planCopy(plan, grant).map((line) => (
          <li key={line}>
            <Check aria-hidden="true" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
      {children}
    </>
  );
  if (onSelect) {
    return (
      <button type="button" className={className} aria-pressed={selected} onClick={onSelect}>
        {body}
      </button>
    );
  }
  return <article className={className}>{body}</article>;
}

/** Shared plan bar controls: period switch (centre column) + currency (right column).
 *  Used in the Billing header and the sign-up top bar (grid: left | centre | right). */
export function StudioPlanBarControls({
  interval,
  onChange,
  maxSave,
  currency,
  onCurrencyChange,
}: {
  interval: BillingPlanInterval;
  onChange: (next: BillingPlanInterval) => void;
  maxSave: number;
  currency: BillingCurrency;
  onCurrencyChange: (next: BillingCurrency) => void;
}) {
  return (
    <>
      <div className="studio-billing-head-center studio-billing-switches">
        <StudioBillingPeriodToggle interval={interval} onChange={onChange} maxSave={maxSave} />
      </div>
      <div className="studio-billing-head-right studio-billing-switches">
        <CurrencyToggle currency={currency} onChange={onCurrencyChange} />
      </div>
    </>
  );
}
